import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../components/ui/InputField';
import CustomButton from '../../components/CustomButton';
import { ARCHIVE_RETENTION_DAYS } from '../../utils/tenantArchived';

/** What the archive dialog knows about the bookings that would block it. */
export interface UpcomingBookingsState {
    /** The count is still being read. */
    loading: boolean;
    /** The read failed — never shown as "no bookings". */
    failed: boolean;
    count: number | null;
}

interface CancelAccountDialogProps {
    /**
     * `archive` (operational-DB tenants, #1379): the account is closed at once.
     * `request` (legacy tenants): the deletion is requested by mail, kolula
     * performs it by hand — the flow from #3581, unchanged.
     */
    mode?: 'archive' | 'request';
    organizationName: string;
    /** Sends the cancel / the deletion request; the dialog stays open either way. */
    onConfirm: () => void;
    onClose: () => void;
    /** The request is in flight. */
    submitting: boolean;
    /** Request mode: the server confirmed both mails went out — only then the success text. */
    submitted: boolean;
    /** Why the request failed, shown inside the dialog. */
    error: string | null;
    /** Archive mode: the upcoming bookings, read before the owner may confirm. */
    upcoming?: UpcomingBookingsState;
    /** Archive mode: days until the data is deleted. */
    retentionDays?: number;
}

/**
 * "Cancel account". Two flows behind one dialog, chosen by the tenant's store:
 *
 * - archive: closes the account at once. Blocked — before the name can even be
 *   typed — while upcoming bookings exist; the count is read up front so the
 *   owner learns it here, not from a refusal. On success the caller signs the
 *   owner out, so this dialog has no success state of its own.
 * - request: "Löschung beantragen" (#1379 interim). The success text appears
 *   only once the server has confirmed the request went out.
 *
 * A failure keeps the form open with the reason in both flows.
 */
const CancelAccountDialog = ({
    mode = 'request', organizationName, onConfirm, onClose, submitting, submitted, error,
    upcoming, retentionDays,
}: CancelAccountDialogProps) => {
    const { t } = useTranslation();
    const [confirmText, setConfirmText] = useState('');
    const isArchive = mode === 'archive';
    const archiveBlocked = isArchive && (
        !upcoming || upcoming.loading || upcoming.failed || upcoming.count === null || upcoming.count > 0
    );
    const isConfirmed = organizationName.trim() !== '' && confirmText === organizationName && !archiveBlocked;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const upcomingNotice = () => {
        if (!isArchive || !upcoming) return null;
        if (upcoming.loading) {
            return <p className={'text-sm text-foreground/60 mb-4'} role={'status'}>{t('account.archive_checking')}</p>;
        }
        if (upcoming.failed || upcoming.count === null) {
            return <p className={'text-sm text-foreground mb-4'} role={'alert'}>{t('account.archive_check_failed')}</p>;
        }
        if (upcoming.count > 0) {
            return (
                <p className={'text-sm text-foreground mb-4'} role={'alert'}>
                    {t('account.archive_blocked', { count: upcoming.count })}
                </p>
            );
        }
        return null;
    };

    const showForm = !isArchive || (upcoming !== undefined && !archiveBlocked);

    return (
        <div className={'fixed inset-0 z-[1000] flex items-center justify-center p-4'}>
            <div className={'fixed inset-0 bg-black bg-opacity-50'} onClick={onClose} />

            <div
                className={'relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md'}
                role={'dialog'}
                aria-modal={'true'}
            >
                {!isArchive && submitted ? (
                    <>
                        <div className={'p-6'}>
                            <h3 className={'text-lg font-bold text-foreground mb-2'}>
                                {t('account.cancel_success_title')}
                            </h3>
                            <p className={'text-sm text-foreground/80'} role={'status'}>
                                {t('account.cancel_success')}
                            </p>
                        </div>
                        <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                            <CustomButton title={t('account.cancel_close_button')} onClick={onClose} />
                        </div>
                    </>
                ) : (
                    <>
                        <div className={'p-6'}>
                            <h3 className={'text-lg font-bold text-red-600 mb-2'}>
                                {t(isArchive ? 'account.archive_dialog_title' : 'account.cancel_dialog_title')}
                            </h3>
                            <p className={'text-sm text-foreground/80 mb-4'}>
                                {isArchive
                                    ? t('account.archive_description', { days: retentionDays ?? ARCHIVE_RETENTION_DAYS })
                                    : t('account.cancel_dialog_description')}
                            </p>
                            {upcomingNotice()}
                            {showForm && (
                                <>
                                    <p className={'text-sm text-foreground/60 mb-4'}>
                                        {t('account.cancel_dialog_confirm_prompt', { name: organizationName })}
                                    </p>
                                    <InputField
                                        label={t('account.organization_name')}
                                        value={confirmText}
                                        onChange={setConfirmText}
                                    />
                                </>
                            )}
                            {error && (
                                <p className={'text-sm text-foreground mt-4'} role={'alert'}>{error}</p>
                            )}
                        </div>

                        <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                            {showForm && (
                                <CustomButton
                                    title={t(isArchive ? 'account.archive_confirm_button' : 'account.cancel_confirm_button')}
                                    onClick={onConfirm}
                                    disabled={!isConfirmed}
                                    loading={submitting}
                                    variant={'delete'}
                                />
                            )}
                            <CustomButton
                                title={t('account.cancel_abort_button')}
                                onClick={onClose}
                                isFilled={false}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CancelAccountDialog;
